import React, { ChangeEvent, FC, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { FlagIcon, FlagIconCode } from 'react-flag-kit';
import arrowDown from '@/assets/icons/downArrow.svg';
import checkIcon from '@/assets/icons/check.svg';
import { P } from './Typography';
import searchIcon from '../assets/icons/searchIcon.svg';

const DropDownContainer = styled.div<{ disabled?: boolean }>`
  position: relative;
  box-sizing: border-box;
  padding-left: 2px;
  background-color: #f4f7f9;
  border-radius: 5px 0 0 5px;
  ${({ disabled }) => disabled && `pointer-events: none;`}
  @media (max-width: 500px) {
    border-radius: 5px 5px 0 0;
  }
`;

const ArrowDown = styled.img``;

const DropDownHeader = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: none;
  padding: 16px;
  min-height: 20px;
  line-height: 20px;
  position: relative;
`;

interface ValueSelectProps {
  isPlaceholder?: boolean;
}

const ValueSelect = styled.span<ValueSelectProps>`
  font-family: Montserrat;
  font-style: normal;
  font-weight: normal;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  display: flex;
  align-items: center;
  color: ${({ isPlaceholder }) => (isPlaceholder ? `#9898AD` : `#13273f`)};
`;

interface DropDownListContainerProps {
  isOpen?: boolean;
}

const DropDownListContainer = styled.div<DropDownListContainerProps>`
  position: absolute;
  margin-top: 10px;
  left: -1px;
  width: 260px !important;
  padding: var(--space-2);
  background: #ffffff;
  box-shadow: 0px 2px 10px rgba(129, 129, 165, 0.35);
  border-radius: 5px;
  width: 100%;
  z-index: 99;
  ${({ isOpen }) => (isOpen ? `border: 1px solid blue;` : ``)}
`;

const DropDownList = styled.ul`
  box-sizing: border-box;
  background: #fff;
  padding: 0 16px;
  margin-left: -16px;
  margin-right: -16px;
  max-height: 300px;
  overflow: auto;
`;

const SearchInput = styled.input`
  border: 1px solid #dbe3eb;
  border-radius: 5px;
  padding: 10px 9px 10px 45px;
  outline: none;
  width: 100%;
  margin-bottom: 10px;
  box-sizing: border-box;
  font-family: Montserrat;
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  ::placeholder {
    color: #8181a5;
  }
`;

const InputIcon = styled.img`
  left: 0;
  position: absolute;
  padding: 5px 8px;
`;

const Category = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-sizing: border-box;
  padding: 10px 25px 10px 16px;
  height: 40px;
  cursor: pointer;
  margin-left: -16px;
  margin-right: -16px;
  &:hover {
    background-color: #f4f7f9;
  }
`;

const CategoryTitleWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const CategoryTitle = styled(P)`
  padding-left: 5px;
`;

const CheckIcon = styled.img``;

const FlagContainer = styled.div`
  margin-right: 10px;
  display: flex;
  align-items: center;
`;

const StyledFlagIcon = styled(FlagIcon)`
  height: 50%;
`;

interface IconCodeProps {
  code: FlagIconCode;
}

const CountryFlagIcon = ({ code }: IconCodeProps) => (
  <FlagContainer>
    <StyledFlagIcon code={code} />
  </FlagContainer>
);

export interface Item {
  label: string;
  value: FlagIconCode;
}

interface DropdownProps {
  withoutSearch?: boolean;
  items: Item[];
  value?: Item;
  placeholder?: string;
  onChange: (value: Item) => void;
  disabled?: boolean;
}

const CountryDropdown: FC<DropdownProps> = ({
  withoutSearch,
  items,
  onChange,
  value,
  placeholder,
  disabled,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchString, setSearchString] = useState<string>(``);
  const ref = useRef(null);
  const valueRef = useRef(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const clickOnWindows = (e: any) => {
      if (
        ref.current !== e.target &&
        e.target !== valueRef.current &&
        e.target !== searchRef.current
      ) {
        setIsOpen(false);
      }
    };
    window.addEventListener(`click`, clickOnWindows);

    return () => window.removeEventListener(`click`, clickOnWindows);
  }, []);

  const handleExpand = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setTimeout(() => {
        searchRef.current?.focus();
      }, 100);
    }
  };

  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    const q = event.target.value;
    setSearchString(q);
  };

  const handleItemClick = (item: Item) => () => {
    onChange(item);
    setIsOpen(!isOpen);
  };

  // eslint-disable-next-line consistent-return
  const filterItems = (item: any) => {
    if (item.label?.toLowerCase().includes(searchString.toLowerCase())) {
      return true;
    }
    if (item.value) {
      return item.value
        .toString()
        .toLowerCase()
        .includes(searchString.toLowerCase());
    }
  };

  return (
    <DropDownContainer disabled={disabled}>
      <DropDownHeader ref={ref} onClick={handleExpand}>
        <ValueSelect ref={valueRef} isPlaceholder={!value && !!placeholder}>
          {value.value && <CountryFlagIcon code={value.value} />}
          {value ? value.label : placeholder}
        </ValueSelect>
        {!disabled && <ArrowDown src={arrowDown} alt="arrowDown" />}
      </DropDownHeader>

      {isOpen && (
        <div>
          {/* added stopPropagation to prevent dropdown close */}
          <DropDownListContainer onClick={(e) => e.stopPropagation()}>
            {!withoutSearch && (
              <div style={{ position: `relative` }}>
                <SearchInput
                  ref={searchRef}
                  value={searchString}
                  onChange={handleSearch}
                  placeholder="Search"
                />
                <InputIcon src={searchIcon} alt="input icon" />
              </div>
            )}
            <DropDownList>
              {items.filter(filterItems).map((el) => (
                <div key={el.label}>
                  <Category onClick={handleItemClick(el as any)}>
                    <CategoryTitleWrapper>
                      <CountryFlagIcon code={(el as any).value} />
                      <CategoryTitle>{el.label}</CategoryTitle>
                    </CategoryTitleWrapper>
                    {value.value === el.value && <CheckIcon src={checkIcon} />}
                  </Category>
                </div>
              ))}
            </DropDownList>
          </DropDownListContainer>
        </div>
      )}
    </DropDownContainer>
  );
};

export default CountryDropdown;
